<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import BillingAPI from 'dashboard/api/billing';
import { useBillingCheckout } from 'dashboard/composables/useBillingCheckout';

const emit = defineEmits(['updated']);

const { t } = useI18n();
const { isPaying, purchaseAiCredits } = useBillingCheckout();

const dialogRef = ref(null);
const payAmount = ref(100);
const unitPrice = ref(null);
const currencySymbol = ref('$');
const currencyCode = ref('USD');

const creditsEstimate = computed(() => {
  const amount = Number(payAmount.value);
  const unit = Number(unitPrice.value);
  if (
    !Number.isFinite(amount) ||
    !Number.isFinite(unit) ||
    amount <= 0 ||
    unit <= 0
  ) {
    return null;
  }
  return Math.floor(amount / unit);
});

const canConfirm = computed(
  () =>
    creditsEstimate.value != null &&
    creditsEstimate.value >= 1 &&
    !isPaying.value
);

const amountLabel = computed(() =>
  currencyCode.value
    ? `${t('GENERAL_SETTINGS.BILLING_USAGE_PAGE.AI_TOPUP_AMOUNT')} (${currencyCode.value})`
    : t('GENERAL_SETTINGS.BILLING_USAGE_PAGE.AI_TOPUP_AMOUNT')
);

const formatMoney = amount =>
  Number(amount).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const loadPricing = async () => {
  try {
    const { data } = await BillingAPI.getAiCredits();
    unitPrice.value = data.unit_price ?? null;
    if (data.currency_symbol) currencySymbol.value = data.currency_symbol;
    if (data.currency_code) currencyCode.value = data.currency_code;
  } catch {
    unitPrice.value = null;
  }
};

const openModal = async () => {
  payAmount.value = 100;
  dialogRef.value?.open();
  await loadPricing();
};

const onConfirm = async () => {
  if (!canConfirm.value) return;

  const ok = await purchaseAiCredits(Number(payAmount.value));
  if (ok) {
    dialogRef.value?.close();
    emit('updated');
  }
};

defineExpose({ open: openModal });
</script>

<template>
  <Dialog
    ref="dialogRef"
    type="edit"
    :title="t('GENERAL_SETTINGS.BILLING_USAGE_PAGE.AI_TOPUP_TITLE')"
    :description="t('GENERAL_SETTINGS.BILLING_USAGE_PAGE.AI_TOPUP_DESCRIPTION')"
    :confirm-button-label="
      t('GENERAL_SETTINGS.BILLING_USAGE_PAGE.AI_TOPUP_PAY')
    "
    :disable-confirm-button="!canConfirm"
    :is-loading="isPaying"
    width="md"
    @confirm="onConfirm"
  >
    <div class="flex flex-col gap-3">
      <Input
        v-model.number="payAmount"
        type="number"
        size="sm"
        :min="0.01"
        :step="0.01"
        :label="amountLabel"
        :disabled="isPaying"
      />
      <p
        v-if="unitPrice != null"
        class="mb-0 text-sm tabular-nums text-n-slate-11"
      >
        {{ t('GENERAL_SETTINGS.BILLING_USAGE_PAGE.AI_TOPUP_UNIT_PRICE') }}
        {{ currencySymbol }}{{ formatMoney(unitPrice) }}
        {{ currencyCode }}
      </p>
      <p
        v-if="creditsEstimate != null && creditsEstimate > 0"
        class="mb-0 text-sm tabular-nums text-n-slate-12"
      >
        {{ t('GENERAL_SETTINGS.BILLING_USAGE_PAGE.AI_TOPUP_CREDITS_ESTIMATE') }}
        {{ creditsEstimate.toLocaleString() }}
        {{ t('GENERAL_SETTINGS.BILLING_USAGE_PAGE.AI_CREDITS_UNIT') }}
      </p>
      <p class="mb-0 text-xs text-n-slate-11">
        {{ t('GENERAL_SETTINGS.BILLING_USAGE_PAGE.AI_TOPUP_KASHIER_NOTE') }}
      </p>
    </div>
  </Dialog>
</template>
